import axiosInstance, { getApiErrorMessage } from "../auth/apiConfig";
import {
  formatRoundTime,
  listAdmittedPatientsForDoctorRound,
  mapWebPatientToAdmissionRow,
  toApiTime,
} from "./doctorRoundService";

const WEB_BASE = "/api/v1/web/tpr";

export {
  formatRoundTime,
  listAdmittedPatientsForDoctorRound,
  mapWebPatientToAdmissionRow,
  toApiTime,
};

export const listTprByPatient = async (regId, admissionId) => {
  try {
    const response = await axiosInstance.get(WEB_BASE, {
      params: { regId, admissionId },
    });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load TPR records"));
  }
};

export const getTprById = async (tprId) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/${tprId}`);
    return response.data?.data ?? null;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load TPR record"));
  }
};

export const createTpr = async (payload) => {
  try {
    const response = await axiosInstance.post(WEB_BASE, payload);
    return response.data?.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to save TPR record"));
  }
};

export const updateTpr = async (tprId, payload) => {
  try {
    const response = await axiosInstance.put(`${WEB_BASE}/${tprId}`, payload);
    return response.data?.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to update TPR record"));
  }
};

export const getTprReport = async (admissionId) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/report`, {
      params: { admissionId },
    });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load TPR report"));
  }
};

const toLong = (value) => {
  if (value === "" || value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

const toDecimal = (value) => {
  if (value === "" || value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toStr = (value) => (value == null ? "" : String(value));

/** Build create/update API payload from RHF form + patient + userId. */
export const mapFormToSavePayload = (formData, patient, userId, { isUpdate } = {}) => {
  const base = {
    date: formData.date,
    time: toApiTime(formData.time),
    bp: toStr(formData.bp),
    pulse: toLong(formData.pulse),
    respiration: toLong(formData.resp),
    temperature: toDecimal(formData.temp),
    sugarPreMeal: toDecimal(formData.preMeal),
    sugarPostMeal: toDecimal(formData.postMeal),
    randomSugar: toDecimal(formData.randomSugar),
    isSugarRandom: formData.sugarType === "random",
    spo2: toStr(formData.spo2),
    o2: toStr(formData.o2),
    otherObse: toStr(formData.otherObs),
    pcv: toStr(formData.pcv),
    platlete: toStr(formData.platelet),
    insuline: toStr(formData.insulin),
    tprWeight: toStr(formData.weight),
    tprAg: toStr(formData.ag),
  };

  if (isUpdate) {
    return {
      ...base,
      updatedBy: userId,
    };
  }

  return {
    regId: Number(patient.regId),
    admissionId: Number(patient.admissionId),
    ...base,
    addedBy: userId,
    updatedBy: userId,
  };
};

/** Map API detail row into RHF form field values. */
export const mapRecordToFormValues = (row) => {
  if (!row) return null;
  return {
    date: row.tprDate || new Date().toISOString().split("T")[0],
    time: formatRoundTime(row.tprTime) || "",
    bp: row.bp != null ? String(row.bp) : "",
    resp: row.respiration != null ? String(row.respiration) : "",
    pulse: row.pulse != null ? String(row.pulse) : "",
    temp: row.temperature != null ? String(row.temperature) : "",
    sugarType: row.isSugarRandom ? "random" : "fpp",
    preMeal: row.sugarPreMeal != null ? String(row.sugarPreMeal) : "",
    postMeal: row.sugarPostMeal != null ? String(row.sugarPostMeal) : "",
    randomSugar: row.randomSugar != null ? String(row.randomSugar) : "",
    spo2: row.spo2 != null ? String(row.spo2) : "",
    o2: row.o2 != null ? String(row.o2) : "",
    pcv: row.pcv != null ? String(row.pcv) : "",
    platelet: row.platlete != null ? String(row.platlete) : "",
    weight: row.tprWeight != null ? String(row.tprWeight) : "",
    ag: row.tprAg != null ? String(row.tprAg) : "",
    otherObs: row.otherObse != null ? String(row.otherObse) : "",
    insulin: row.insuline != null ? String(row.insuline) : "",
    addedBy: row.addedByName || row.userName || "",
  };
};

/** Normalize list row for the vitals history table. */
export const mapListRowForTable = (row) => ({
  id: row.tprId,
  tprId: row.tprId,
  date: row.tprDate || "—",
  time: formatRoundTime(row.tprTime) || "—",
  bp: row.bp || "—",
  pulse: row.pulse != null ? String(row.pulse) : "—",
  temp: row.temperature != null ? String(row.temperature) : "—",
  resp: row.respiration != null ? String(row.respiration) : "—",
  addedBy: row.addedByName || row.userName || "—",
});
